import { useState, useEffect, useRef } from "react";
import Draggable from "react-draggable";
import { axInst2 } from "../../Components/Login/Service";
import { FaThumbsUp, FaThumbsDown } from "react-icons/fa";
import { FaArrowUpRightFromSquare, FaMaximize } from "react-icons/fa6";

const AiChatBotModal = ({ handleModal }) => {
  const [isMinimized, setIsMinimized] = useState(false);

  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hello 👋, I am CollabX Assistant. How can I help you?",
      feedback: null,
      showFeedback: false,
    },
  ]);

  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  /* ------------------ ESC close ------------------ */
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") handleModal(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [handleModal]);

  /* ------------------ Utils ------------------ */
  const sanitizeInput = (input) =>
    input.replace(/[^a-zA-Z0-9.\s\u0900-\u097F]/g, "");

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(scrollToBottom, [messages]);

  /* ------------------ Typing Simulation ------------------ */
  const simulateTyping = (fullText) => {
    setIsTyping(true);
    let index = 0;

    setMessages((prev) => [
      ...prev,
      {
        sender: "bot",
        text: "",
        feedback: null,
        showFeedback: true,
      },
    ]);

    const typingInterval = setInterval(() => {
      index++;
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1].text = fullText.slice(0, index);
        return updated;
      });

      if (index >= fullText.length) {
        clearInterval(typingInterval);
        setIsTyping(false);
      }
    }, 30);
  };

  /* ------------------ Send Message ------------------ */
  const handleSend = async () => {
    if (!input.trim()) return;

    setMessages((prev) => [...prev, { sender: "user", text: input }]);
    setInput("");

    try {
      setLoading(true);
      const response = await axInst2.post("query", { query: input });
      simulateTyping(response.data || "⚠️ No response received.");
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "⚠️ Request error.",
          feedback: null,
          showFeedback: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------ Feedback ------------------ */
  const handleFeedback = async (index, rating) => {
    setMessages((prev) =>
      prev.map((msg, i) => (i === index ? { ...msg, feedback: rating } : msg)),
    );

    try {
      await axInst2.post("feedback", { rating });
    } catch (err) {
      console.error("Feedback API error:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center md:justify-end md:pr-10">
      {/* ------------------ MINIMIZED VIEW ------------------ */}
      {isMinimized ? (
        <div className="fixed bottom-4 right-4 bg-gradient-to-r from-[#2A5775] to-[#2C89CB] shadow-lg rounded-lg w-[300px] h-[50px] flex items-center justify-between px-4">
          <span className="font-medium text-white">CollabX Assistant</span>
          <div className="flex gap-2">
            <button
              onClick={() => setIsMinimized(false)}
              title="Maximize"
              className="text-white"
            >
              <FaMaximize />
            </button>
            <button
              onClick={() => handleModal(false)}
              title="Close"
              className="text-white rounded-full bg-[#E5E5E570] w-6 h-6 flex items-center justify-center"
            >
              ✕
            </button>
          </div>
        </div>
      ) : (
        /* ------------------ FULL MODAL ------------------ */
        <Draggable handle=".modal-header" cancel="button" bounds="parent">
          <div className="bg-white rounded-lg shadow-lg relative flex flex-col w-[90vw] sm:w-[16rem] md:w-[20rem] lg:w-[28rem] max-w-[95vw]">
            {/* Header */}
            <div className="modal-header cursor-move bg-gradient-to-r from-[#2A5775] to-[#2C89CB] px-4 py-2 rounded-t-lg flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">
                CollabX Assistant
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsMinimized(true)}
                  title="Minimize"
                  className="text-white"
                >
                  <FaArrowUpRightFromSquare />
                </button>
                <button
                  onClick={() => handleModal(false)}
                  className="text-white rounded-full bg-[#E5E5E570] w-6 h-6 flex items-center justify-center"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 p-4 overflow-y-auto max-h-80 space-y-3 scrollbar-none">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${
                    msg.sender === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div className="flex flex-col gap-1 max-w-[70%]">
                    <div
                      className={`px-3 py-2 rounded-lg text-sm ${
                        msg.sender === "user"
                          ? "bg-gradient-to-r from-[#2A5775] to-[#2C89CB] text-white"
                          : "bg-gray-200 text-gray-800"
                      }`}
                    >
                      {msg.text}
                    </div>

                    {msg.sender === "bot" && msg.showFeedback && !isTyping && (
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => handleFeedback(i, 1)}
                          className={`transition-all duration-200 transform active:scale-[2.25] ${
                            msg.feedback === 1
                              ? "text-green-500 scale-110"
                              : "text-gray-400 hover:scale-110"
                          }`}
                        >
                          <FaThumbsUp />
                        </button>
                        <button
                          onClick={() => handleFeedback(i, -1)}
                          className={`transition-all duration-200 transform active:scale-[2.25] ${
                            msg.feedback === -1
                              ? "text-red-400 scale-110"
                              : "text-gray-400 hover:scale-110"
                          }`}
                        >
                          <FaThumbsDown />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="text-sm text-gray-400 italic">
                  Assistant is thinking...
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(sanitizeInput(e.target.value))}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Type a message..."
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
              />
              <button
                onClick={handleSend}
                disabled={loading}
                className="bg-gradient-to-r from-[#2A5775] to-[#2C89CB] text-white px-4 py-2 rounded-lg"
              >
                Send
              </button>
            </div>
          </div>
        </Draggable>
      )}
    </div>
  );
};

export default AiChatBotModal;
